import { useNavigate } from "react-router-dom";
import { useFunds, useCreateFund } from "@/hooks/useFunds";
import { useInvestments } from "@/hooks/useInvestments";
import { useAllFundAssets } from "@/hooks/useFundAssets";
import { useMe } from "@/hooks/useMe";
import { PageHeader } from "@/components/PageHeader";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Plus, Check, X } from "lucide-react";
import { formatBRL, formatUSD } from "@/lib/utils";
import { toast } from "sonner";
import { useState } from "react";
import type { Fund } from "@/types";

export default function Funds() {
  const navigate = useNavigate();
  const { data: funds, isLoading } = useFunds();
  const { data: investments } = useInvestments();
  const { data: allAssets } = useAllFundAssets();
  const { data: me } = useMe();
  const isAdmin = me?.user.isAdmin ?? false;
  const canEditFunds = isAdmin || (me?.permissions?.can_edit_funds ?? false);

  const createFund = useCreateFund();
  const [addMode, setAddMode] = useState(false);
  const [newName, setNewName] = useState("");

  const totals = (fundId: string) => {
    const rows = (investments ?? []).filter((i) => i.fund_id === fundId);
    const brl = rows.reduce((s, r) => s + Number(r.amount_brl), 0);
    const usd = rows.reduce((s, r) => s + Number(r.amount_usd), 0);
    return { brl, usd, count: rows.length };
  };

  const pctInvestido = (fund: Fund) => {
    const t = totals(fund.id);
    const usdBrl = Number(fund.usdbrl_rate ?? 0) * t.usd;
    const totalCommitted = t.brl + usdBrl;
    const acquisitionTotal = (allAssets ?? [])
      .filter((a) => a.fund_id === fund.id)
      .reduce((s, a) => s + Number(a.acquisition_cost ?? 0), 0) * 1_000_000;
    return totalCommitted > 0 ? (acquisitionTotal / totalCommitted) * 100 : 0;
  };

  const saveNew = async () => {
    if (!newName.trim()) return;
    try {
      await createFund.mutateAsync({ name: newName.trim() });
      toast.success("Fund created");
    } catch {
      toast.error("Error creating fund");
    }
    setAddMode(false);
    setNewName("");
  };

  return (
    <div className="min-h-screen bg-background">
      <PageHeader
        title="IR Jus Capital"
        subtitle="Funds"
        actions={
          canEditFunds ? (
            <Button size="sm" onClick={() => setAddMode(true)} className="gap-2">
              <Plus className="h-4 w-4" /> New Fund
            </Button>
          ) : undefined
        }
      />
      <div className="container mx-auto px-4 py-8">
        {isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : (
          <div className="rounded-lg border border-border bg-card overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow style={{ backgroundColor: "rgb(212, 163, 123)" }} className="hover:opacity-90">
                  <TableHead style={{ color: "#fff", fontWeight: 600 }}>Fund</TableHead>
                  <TableHead className="text-right" style={{ color: "#fff", fontWeight: 600 }}>Committed BRL</TableHead>
                  <TableHead className="text-right" style={{ color: "#fff", fontWeight: 600 }}>Committed USD</TableHead>
                  <TableHead className="text-right" style={{ color: "#fff", fontWeight: 600 }}>% Invested</TableHead>
                  <TableHead className="text-right" style={{ color: "#fff", fontWeight: 600 }}>Investors</TableHead>
                  <TableHead className="text-right" style={{ color: "#fff", fontWeight: 600 }}>Assets</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {addMode && (
                  <TableRow>
                    <TableCell colSpan={2}>
                      <Input
                        autoFocus
                        placeholder="Fund name"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") saveNew();
                          if (e.key === "Escape") setAddMode(false);
                        }}
                        className="h-8"
                      />
                    </TableCell>
                    <TableCell colSpan={3} />
                    <TableCell>
                      <div className="flex gap-1 justify-end">
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7"
                          onClick={saveNew}
                          disabled={createFund.isPending}
                        >
                          <Check className="h-4 w-4 text-green-600" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7"
                          onClick={() => setAddMode(false)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                )}

                {(funds ?? []).map((f) => {
                  const t = totals(f.id);
                  const pct = pctInvestido(f);
                  const assetCount = (allAssets ?? []).filter((a) => a.fund_id === f.id).length;

                  return (
                    <TableRow
                      key={f.id}
                      className="cursor-pointer hover:bg-muted/40"
                      onClick={() => navigate(`/fund/${f.id}`)}
                    >
                      <TableCell className="font-semibold text-primary hover:underline">
                        {f.name}
                      </TableCell>
                      <TableCell className="text-right">{formatBRL(t.brl)}</TableCell>
                      <TableCell className="text-right">{formatUSD(t.usd)}</TableCell>
                      <TableCell className="text-right">{Math.abs(pct).toFixed(1)}%</TableCell>
                      <TableCell className="text-right">{t.count}</TableCell>
                      <TableCell className="text-right">{assetCount}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
